/**
 * proximity-chat
 *
 * WebRTC-based spatial voice chat with distance attenuation.
 * Uses the Colyseus lobby as the signaling channel
 * and establishes peer-to-peer audio connections between players.
 *
 * Integration:
 *   - Call `initMic()` once after the lobby is joined.
 *   - Call `createPeer(sessionId, true, lobby)` when a remote player joins
 *     (the player with the lexicographically lower userId initiates).
 *   - Route incoming `rtcOffer` / `rtcAnswer` / `rtcIce` messages to `handleSignal()`.
 *   - Call `updatePositions(localPos, remotePlayerMap)` every frame from `update(dt)`.
 *   - Call `removePeer(sessionId)` when a remote player disconnects.
 *   - Call `destroy()` on session end to release mic & close all peers.
 */
import { vec3, quat } from 'gl-matrix';

const ICE_SERVERS = [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
];

/** Distance (units) beyond which a peer is fully silent. */
const MAX_DISTANCE = 30.0;

/** Distance (units) within which volume is at 100 %. */
const FALLOFF_START = 5.0;

/** Base gain applied to every peer (> 1 to compensate for distance attenuation). */
const VOICE_GAIN = 2.0;

/** Inner / outer cone angles (degrees) – voices are loudest in front. */
const CONE_INNER = 360;
const CONE_OUTER = 360;
/** Gain applied outside the outer cone (0 = silent behind). */
const CONE_OUTER_GAIN = 0.4;

export class ProximityChat {
    constructor() {
        /** @type {Map<string, {pc: RTCPeerConnection, gainNode: GainNode, pannerNode: PannerNode, source: MediaStreamAudioSourceNode|null}>} */
        this.peers = new Map();
        /** @type {MediaStream|null} */
        this.localStream = null;
        /** @type {AudioContext|null} */
        this.audioCtx = null;
        /** @type {boolean} */
        this.muted = false;
        /** Reusable vec for position reads */
        this._tempPos = vec3.create();
        this._connectionQueue = [];
        this._isProcessingQueue = false;
        this._tempPos = vec3.create();
        this._forward = vec3.fromValues(0, 0, -1);
        this._up = vec3.fromValues(0, 1, 0);
        this._worldForward = vec3.create();
        this._worldUp = vec3.create();
    }

    /**
     * Lazily create the AudioContext and ensure it's running.
     * Registers global user-gesture listeners to resume if suspended.
     */
    _ensureAudioCtx() {
        if (!this.audioCtx) {
            this.audioCtx = new AudioContext();

            // PRIME THE CONTEXT: Play a 0-volume beep immediately to warm up the driver
            const osc = this.audioCtx.createOscillator();
            const silentGain = this.audioCtx.createGain();
            silentGain.gain.value = 0;
            osc.connect(silentGain).connect(this.audioCtx.destination);
            osc.start();
            osc.stop(this.audioCtx.currentTime + 0.1);

            // Always register the resume handler — not just when starting suspended.
            // The context can be created running and later suspended by the browser
            // (tab visibility change, background throttle, etc.).
            const resume = () => {
                if (this.audioCtx && this.audioCtx.state === 'suspended') {
                    this.audioCtx.resume();
                }
                if (!this.audioCtx || this.audioCtx.state === 'running') {
                    document.removeEventListener('click', resume);
                    document.removeEventListener('touchstart', resume);
                    document.removeEventListener('keydown', resume);
                    window.removeEventListener('vrdisplayactivate', resume);
                }
            };
            document.addEventListener('click', resume, { passive: true });
            document.addEventListener('touchstart', resume, { passive: true });
            document.addEventListener('keydown', resume, { passive: true });
            window.addEventListener('vrdisplayactivate', resume, { passive: true });
            this._resumeHandler = resume;
        }
        if (this.audioCtx.state === 'suspended') {
            this.audioCtx.resume();
        }
        return this.audioCtx;
    }

    // ─── Public API ──────────────────────────────────────────────────

    /**
     * Acquire the microphone stream.  Prefers a stream already obtained by
     * the page-level toggle (stored on `window._proxChat.stream`).
     * Falls back to requesting a new one if none exists.
     * @returns {Promise<boolean>} true if a mic stream is available.
     */
    async initMic() {
        // Reuse stream already captured by the page-level toggle
        if (window._proxChat && window._proxChat.stream) {
            this.localStream = window._proxChat.stream;
            this.muted = !window._proxChat.enabled;
            return true;
        }
        try {
            this.localStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });
            return true;
        } catch (err) {
            console.warn('[proximity-chat] Mic access denied or unavailable:', err.message);
            return false;
        }
    }

    /**
     * Replace the local mic stream (e.g. after a re-request from the UI toggle).
     * @param {MediaStream} stream
     */
    setStream(stream) {
        this.localStream = stream;
        // Update tracks on existing peer connections
        for (const [, peer] of this.peers) {
            const senders = peer.pc.getSenders();
            const audioTrack = stream.getAudioTracks()[0];
            if (audioTrack) {
                for (let i = 0; i < senders.length; i++) {
                    if (senders[i].track && senders[i].track.kind === 'audio') {
                        senders[i].replaceTrack(audioTrack);
                    }
                }
            }
        }
    }

    /**
     * Set up a WebRTC peer connection with a remote player.
     *
     * @param {string} sessionId  The remote player's session/user ID.
     * @param {boolean} isInitiator  true if WE should create the offer.
     * @param {object} lobby  The Arena lobby handle (needs `lobby.data.sendToAll`).
     * @param {string} localId  Our own userId for targeting.
     */
    async createPeer(sessionId, isInitiator, lobby, localId) {
        if (this.peers.has(sessionId)) return;
        // If we are already connecting someone, queue this request
        if (this._isProcessingQueue) {
            this._connectionQueue.push({ sessionId, isInitiator, lobby, localId });
            return;
        }
        this._isProcessingQueue = true;


        // Always keep the latest lobby reference at class level so reconnect
        // retries use the live lobby, not a stale captured parameter.
        this._lobby = lobby;

        const ctx = this._ensureAudioCtx();
        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

        // ── Spatial audio nodes ──
        const pannerNode = ctx.createPanner();
        pannerNode.panningModel = 'HRTF';
        pannerNode.distanceModel = 'linear';
        pannerNode.refDistance = FALLOFF_START;
        pannerNode.maxDistance = MAX_DISTANCE;
        pannerNode.rolloffFactor = 1;
        // Cone angles are both 360° → fully omnidirectional (no directional attenuation).
        pannerNode.coneInnerAngle = CONE_INNER;
        pannerNode.coneOuterAngle = CONE_OUTER;
        pannerNode.coneOuterGain = CONE_OUTER_GAIN;

        const gainNode = ctx.createGain();
        gainNode.gain.value = this.muted ? 0 : VOICE_GAIN;
        pannerNode.connect(gainNode).connect(ctx.destination);

        // pendingIce buffers candidates that arrive before setRemoteDescription.
        const peer = { pc, gainNode, pannerNode, source: null, audioEl: null, pendingIce: [] };
        this.peers.set(sessionId, peer);

        // ── Attach local mic tracks ──
        if (this.localStream) {
            const tracks = this.localStream.getAudioTracks();
            for (let i = 0; i < tracks.length; i++) {
                pc.addTrack(tracks[i], this.localStream);
            }
        }

        // ── Receive remote audio and route through spatial nodes ──
        pc.ontrack = (event) => {
            if (event.streams && event.streams[0]) {
                const stream = event.streams[0];

                // Chrome requires a remote MediaStream attached to a playing <audio>
                // element before createMediaStreamSource produces output.
                // Append to body so the element is not GC-eligible mid-session.
                const audio = document.createElement('audio');
                audio.srcObject = stream;
                audio.setAttribute('autoplay', '');
                audio.setAttribute('playsinline', '');
                audio.volume = 0; // Actual output goes through Web Audio gain/panner nodes.
                document.body.appendChild(audio);
                audio.play().catch(() => { });
                peer.audioEl = audio;

                peer.source = ctx.createMediaStreamSource(stream);
                peer.source.connect(pannerNode);

                if (ctx.state === 'suspended') {
                    ctx.resume();
                }
            }
        };
        // ── ICE candidate → signal via Colyseus ──
        pc.onicecandidate = (event) => {
            if (event.candidate && lobby) {
                lobby.data.sendToAll({
                    type: 'rtcIce',
                    payload: {
                        from: localId,
                        target: sessionId,
                        candidate: event.candidate.toJSON(),
                    },
                });
            }
        };

        // ── Reconnect on ICE / connection failure ──
        let retryCount = 0;
        const MAX_RETRIES = 3;
        let disconnectGraceTimer = null;
        pc.onconnectionstatechange = () => {
            const state = pc.connectionState;
            if (state === 'disconnected') {
                // 'disconnected' is often transient — give ICE time to recover
                if (!disconnectGraceTimer) {
                    disconnectGraceTimer = setTimeout(() => {
                        disconnectGraceTimer = null;
                        // Only escalate if still disconnected (not recovered or already failed)
                        const current = this.peers.get(sessionId);
                        if (current && current.pc.connectionState === 'disconnected') {
                            handleReconnect();
                        }
                    }, 5000);
                }
            } else if (state === 'failed') {
                if (disconnectGraceTimer) { clearTimeout(disconnectGraceTimer); disconnectGraceTimer = null; }
                handleReconnect();
            } else if (state === 'connected') {
                // Connection recovered — reset retry counter and cancel grace timer
                retryCount = 0;
                if (disconnectGraceTimer) { clearTimeout(disconnectGraceTimer); disconnectGraceTimer = null; }
            }
        };
        const handleReconnect = () => {
            // 1. Validate that we are still in a valid game state
            const currentLobby = this._lobby;
            if (!currentLobby || !currentLobby.data) {
                console.log("[proximity-chat] Aborting reconnect: Lobby is null or destroyed.");
                return;
            }

            if (retryCount >= MAX_RETRIES) {
                this.removePeer(sessionId);
                return;
            }
            retryCount++;
            console.warn('[proximity-chat] Peer', sessionId, 'reconnect attempt', retryCount, '/', MAX_RETRIES);
            this.removePeer(sessionId);
            setTimeout(() => {
                // Use this._lobby (live reference) instead of the captured `lobby`
                // parameter, which may point to a dead lobby after a session restart.
                const currentLobby = this._lobby;
                if (this.peers.has(sessionId) || !this.audioCtx || !currentLobby) return;
                const shouldInitiate = localId < sessionId;
                this.createPeer(sessionId, shouldInitiate, currentLobby, localId);
            }, 1500 * retryCount);
        };

        // ── If we're the initiator, send an offer ──
        if (isInitiator) {
            try {
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                lobby.data.sendToAll({
                    type: 'rtcOffer',
                    payload: {
                        from: localId,
                        target: sessionId,
                        sdp: { type: pc.localDescription.type, sdp: pc.localDescription.sdp },
                    },
                });
            } catch (err) {
                console.warn('[proximity-chat] Failed to create offer for', sessionId, err.message);
            }
        }
        // At the very end of the function:
        setTimeout(() => {
            this._isProcessingQueue = false;
            if (this._connectionQueue.length > 0) {
                const next = this._connectionQueue.shift();
                this.createPeer(next.sessionId, next.isInitiator, next.lobby, next.localId);
            }
        }, 200); // 200ms gap prevents the CPU from choking on WebRTC handshakes
    }

    /**
     * Handle an incoming signaling message (rtcOffer / rtcAnswer / rtcIce).
     * Call this from arena-handler's onReceiveAll handler.
     *
     * @param {object} data  The raw message object ({ type, payload }).
     * @param {object} lobby  The Arena lobby handle.
     * @param {string} localId  Our own userId.
     */
    async handleSignal(data, lobby, localId) {
        const { type, payload } = data;
        if (!payload || payload.target !== localId) return;

        const remoteId = payload.from;

        if (type === 'rtcOffer') {
            await this.createPeer(remoteId, false, lobby, localId);
            const peer = this.peers.get(remoteId);
            if (!peer) return;
            try {
                await peer.pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
                // Flush any ICE candidates that arrived before setRemoteDescription.
                for (const candidate of peer.pendingIce) {
                    await peer.pc.addIceCandidate(candidate).catch((err) => {
                        console.debug('[proximity-chat] Flushed ICE add failed:', err.message);
                    });
                }
                peer.pendingIce = [];

                const answer = await peer.pc.createAnswer();
                await peer.pc.setLocalDescription(answer);
                lobby.data.sendToAll({
                    type: 'rtcAnswer',
                    payload: {
                        from: localId,
                        target: remoteId,
                        sdp: { type: peer.pc.localDescription.type, sdp: peer.pc.localDescription.sdp },
                    },
                });
            } catch (err) {
                console.warn('[proximity-chat] Failed to handle offer from', remoteId, err.message);
            }
        } else if (type === 'rtcAnswer') {
            const peer = this.peers.get(remoteId);
            if (!peer) return;
            try {
                await peer.pc.setRemoteDescription(new RTCSessionDescription(payload.sdp));
                // Flush buffered ICE candidates now that remote description is set.
                for (const candidate of peer.pendingIce) {
                    await peer.pc.addIceCandidate(candidate).catch((err) => {
                        console.debug('[proximity-chat] Flushed ICE add failed:', err.message);
                    });
                }
                peer.pendingIce = [];
            } catch (err) {
                console.warn('[proximity-chat] Failed to handle answer from', remoteId, err.message);
            }
        } else if (type === 'rtcIce') {
            const peer = this.peers.get(remoteId);
            if (!peer) return;
            const candidate = new RTCIceCandidate(payload.candidate);
            // Buffer the candidate if remote description is not yet set.
            if (!peer.pc.remoteDescription) {
                peer.pendingIce.push(candidate);
            } else {
                try {
                    await peer.pc.addIceCandidate(candidate);
                } catch (err) {
                    console.debug('[proximity-chat] ICE add failed (non-fatal):', err.message);
                }
            }
        }
    }

    /**
     * Update the AudioContext listener (local player) position and each
     * remote peer's panner position so spatial audio tracks distance.
     *
     * Call this every frame from the arena-handler update() loop.
     *
     * @param {Float32Array} localPos  The local player world position [x, y, z].
     * @param {Map<string, object>} remotePlayerMap  sessionId → WLE scene object.
     */
    /**
     * @param {Float32Array} localPos Local player world position
     * @param {Float32Array} localRot Local player world rotation (quat)
     * @param {Map<string, object>} remotePlayerMap sessionId -> Scene Object
     */
    updatePositions(localPos, localRot, remotePlayerMap) {
        if (!this.audioCtx || this.peers.size === 0) return;

        const listener = this.audioCtx.listener;

        // 1. Update Listener Position
        if (listener.positionX) {
            listener.positionX.value = localPos[0];
            listener.positionY.value = localPos[1];
            listener.positionZ.value = localPos[2];
        } else {
            listener.setPosition(localPos[0], localPos[1], localPos[2]);
        }

        // 2. Update Listener Orientation (Rotation)
        // Transform the default forward/up vectors by the current head rotation
        vec3.transformQuat(this._worldForward, this._forward, localRot);
        vec3.transformQuat(this._worldUp, this._up, localRot);

        if (listener.forwardX) {
            listener.forwardX.value = this._worldForward[0];
            listener.forwardY.value = this._worldForward[1];
            listener.forwardZ.value = this._worldForward[2];
            listener.upX.value = this._worldUp[0];
            listener.upY.value = this._worldUp[1];
            listener.upZ.value = this._worldUp[2];
        } else {
            listener.setOrientation(
                this._worldForward[0], this._worldForward[1], this._worldForward[2],
                this._worldUp[0], this._worldUp[1], this._worldUp[2]
            );
        }

        // 3. Update Remote Peer Positions
        for (const [sessionId, peer] of this.peers) {
            const remote = remotePlayerMap.get(sessionId);
            if (!remote) continue;

            let pos;
            if (remote.currentPos) {
                pos = remote.currentPos;
            } else if (remote.getPositionWorld) {
                remote.getPositionWorld(this._tempPos);
                pos = this._tempPos;
            } else {
                pos = this._tempPos;
            }

            const p = peer.pannerNode;
            if (p.positionX) {
                p.positionX.value = pos[0];
                p.positionY.value = pos[1];
                p.positionZ.value = pos[2];
            } else {
                p.setPosition(pos[0], pos[1], pos[2]);
            }
        }
    }

    /**
     * Toggle proximity chat on/off. Mutes both outgoing mic and incoming audio.
     * @param {boolean} [force] If provided, set disabled to this value.
     * @returns {boolean} true if proximity chat is now disabled.
     */
    toggleMute(force) {
        this.muted = force !== undefined ? force : !this.muted;
        // Mute outgoing mic
        if (this.localStream) {
            const tracks = this.localStream.getAudioTracks();
            for (let i = 0; i < tracks.length; i++) {
                tracks[i].enabled = !this.muted;
            }
        }
        // Mute/unmute all incoming peer audio
        for (const [, peer] of this.peers) {
            peer.gainNode.gain.value = this.muted ? 0 : VOICE_GAIN;
        }
        return this.muted;
    }

    /**
     * Tear down a single peer (e.g. when a player disconnects).
     * @param {string} sessionId
     */
    removePeer(sessionId) {
        const peer = this.peers.get(sessionId);
        if (!peer) return;
        try {
            if (peer.audioEl) {
                peer.audioEl.pause();
                peer.audioEl.srcObject = null;
                // Remove from DOM so the element can be GC'd cleanly.
                if (peer.audioEl.parentNode) {
                    peer.audioEl.parentNode.removeChild(peer.audioEl);
                }
                peer.audioEl = null;
            }
            if (peer.source) {
                peer.source.disconnect();
            }
            peer.pannerNode.disconnect();
            peer.gainNode.disconnect();
            peer.pc.close();
        } catch (_) { /* already closed */ }
        this.peers.delete(sessionId);
    }

    /**
     * Full cleanup — release mic, close all peers, close AudioContext.
     */
    destroy() {
        const peerIds = [...this.peers.keys()];
        for (let i = 0; i < peerIds.length; i++) {
            this.removePeer(peerIds[i]);
        }
        if (this._resumeHandler) {
            document.removeEventListener('click', this._resumeHandler);
            document.removeEventListener('touchstart', this._resumeHandler);
            document.removeEventListener('keydown', this._resumeHandler);
            window.removeEventListener('vrdisplayactivate', this._resumeHandler);
            this._resumeHandler = null;
        }
        if (this.localStream) {
            const tracks = this.localStream.getTracks();
            for (let i = 0; i < tracks.length; i++) {
                tracks[i].stop();
            }
            this.localStream = null;
        }
        if (this.audioCtx) {
            this.audioCtx.close().catch(() => { });
            this.audioCtx = null;
        }
    }
}
